'use client'

import { useEffect, useRef } from 'react'

const HYDRATION_ERROR_PATTERNS = [
	'hydration',
	'text content does not match',
	'expected server html',
	'did not expect server html',
	'server rendered',
	'minified react error #418',
	'minified react error #419',
	'minified react error #422',
	'minified react error #423',
	'minified react error #425',
	'initial ui does not match',
	'contain a matching'
]

// Global storage for hydration errors captured before React mounts
const globalHydrationErrors: any = []
const reportedHashes = new Set<string>()

const isHydrationError = (message: string): boolean => {
	const lowerMessage = message.toLowerCase()
	return HYDRATION_ERROR_PATTERNS.some(pattern => lowerMessage.includes(pattern))
}

const extractContentMismatch = (message: string): { serverContent?: string; clientContent?: string } => {
	const result: { serverContent?: string; clientContent?: string } = {}

	const serverMatch = message.match(/Server:\s*["']?([^"'\n]+)["']?/i)
	if (serverMatch) {
		result.serverContent = serverMatch[1]
	}

	const clientMatch = message.match(/Client:\s*["']?([^"'\n]+)["']?/i)
	if (clientMatch) {
		result.clientContent = clientMatch[1]
	}

	return result
}

const extractComponentStack = (args: unknown[]): string | undefined => {
	for (const arg of args) {
		if (typeof arg === 'string' && arg.includes('at ') && arg.includes('\n')) {
			return arg
		}
		if (arg instanceof Error && arg.stack) {
			return arg.stack
		}
	}
	return undefined
}

const captureHydrationError = (message: string, componentStack?: string): void => {
	const errorHash = message.slice(0, 200)
	if (reportedHashes.has(errorHash)) return
	reportedHashes.add(errorHash)

	const { serverContent, clientContent } = extractContentMismatch(message)
	globalHydrationErrors.push({
		message,
		serverContent,
		clientContent,
		componentStack
	})
}

// Install interceptor immediately when module loads (before React)
if (typeof window !== 'undefined') {
	const originalError = console.error

	console.error = (...args: unknown[]): void => {
		originalError.apply(console, args)

		const message = args
			.map(arg => {
				if (typeof arg === 'string') return arg
				if (arg instanceof Error) return arg.message
				try {
					return JSON.stringify(arg)
				} catch {
					return String(arg)
				}
			})
			.join(' ')

		if (isHydrationError(message)) {
			captureHydrationError(message, extractComponentStack(args))
		}
	}

	// Also listen for global errors
	window.addEventListener('error', (event: ErrorEvent) => {
		const message = event.message || event.error?.message || ''
		if (isHydrationError(message)) {
			captureHydrationError(message, event.error?.stack)
		}
	})
}

interface UseHydrationErrorInterceptorOptions {
	onHydrationError: (error: any) => void
}

export const useHydrationErrorInterceptor = ({ onHydrationError }: UseHydrationErrorInterceptorOptions): void => {
	const processedRef = useRef(false)

	useEffect(() => {
		// Process any errors captured before React mounted
		if (!processedRef.current && globalHydrationErrors.length > 0) {
			processedRef.current = true
			globalHydrationErrors.forEach(error => onHydrationError(error))
		}

		// Continue listening for new errors
		const interval = setInterval(() => {
			if (globalHydrationErrors.length > 0) {
				const newErrors = globalHydrationErrors.splice(0)
				newErrors.forEach(error => onHydrationError(error))
			}
		}, 500)

		return () => clearInterval(interval)
	}, [onHydrationError])
}
